import { useId } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Event } from "@lezz/shared";
import { apiFetch } from "@/shared/lib/bff/client";
import { useRealtimeInvalidation } from "@/shared/hooks/use-realtime-invalidation";
import type { ParticipantPreview } from "@/features/events/lib/attach-participants-preview";

/**
 * `/api/events` response shape: a strict superset of `Event` with an embedded,
 * capped participant preview so the list can render avatars/counts and sort by
 * popularity without a per-card N+1 (see docs/places/pr-05-events-n1-fix.md).
 */
export type EventWithParticipantsPreview = Event & {
  participantsPreview: ParticipantPreview[];
  participantCount: number;
};

export async function fetchUpcomingEvents(): Promise<
  EventWithParticipantsPreview[]
> {
  return apiFetch<EventWithParticipantsPreview[]>("/api/events");
}

export const eventKeys = {
  all: ["events"] as const,
  upcoming: () => [...eventKeys.all, "upcoming"] as const,
  search: (query: string) =>
    [...eventKeys.all, "search", "with-past", query] as const,
  detail: (id: string) => [...eventKeys.all, "detail", id] as const,
};

export function useUpcomingEvents() {
  const instanceId = useId();

  useRealtimeInvalidation(`events:upcoming:${instanceId}`, "events", [
    eventKeys.upcoming(),
  ]);
  useRealtimeInvalidation(
    `events:upcoming:participants:${instanceId}`,
    "event_participants",
    [eventKeys.upcoming()]
  );

  return useQuery({
    queryKey: eventKeys.upcoming(),
    queryFn: fetchUpcomingEvents,
  });
}

export interface EventSearchResults {
  upcoming: Event[];
  past: Event[];
}

export async function fetchEventsByQuery(
  query: string
): Promise<EventSearchResults> {
  return apiFetch<EventSearchResults>(
    `/api/events/search?q=${encodeURIComponent(query.trim())}&includePast=true`
  );
}

export function useSearchEvents(query: string) {
  const normalizedQuery = query.trim();
  return useQuery({
    queryKey: eventKeys.search(normalizedQuery),
    queryFn: () => fetchEventsByQuery(normalizedQuery),
    enabled: Boolean(normalizedQuery.length > 0),
  });
}

export async function fetchEventById(eventId: string): Promise<Event> {
  return apiFetch<Event>(`/api/events/${eventId}`);
}

export function useEvent(eventId: string) {
  return useQuery({
    queryKey: eventKeys.detail(eventId),
    queryFn: () => fetchEventById(eventId),
    enabled: Boolean(eventId),
  });
}

export async function fetchPublicEventById(eventId: string): Promise<Event> {
  return apiFetch<Event>(`/api/events/public/${eventId}`);
}

/**
 * Reads an event from the public (anon) endpoint. Used by the logged-out
 * event detail page. Shares the `eventKeys.detail` cache key with `useEvent`
 * so the cache hands off cleanly once the user logs in.
 */
export function usePublicEvent(eventId: string) {
  return useQuery({
    queryKey: eventKeys.detail(eventId),
    queryFn: () => fetchPublicEventById(eventId),
    enabled: Boolean(eventId),
  });
}
